export enum AccountType {
  PASSENGER = "passenger",
  EXECUTIVE_ASSISTANT = "executive_assistant",
  TRAVEL_MANAGER = "travel_manager",
  DRIVER = "driver",
  OPERATOR = "operator",
  USER_ACCOUNT = "user_account",
}

/**
 * True when an account type manages other clients — i.e. an Executive Assistant or a Travel Manager.
 * Shared by `useCanManageAsAssistant` (logged-in account) and the operator Manage-Client view (managed account).
 *
 * Callers pass the payload's `account_type`, which the backend derives straight from the
 * authoritative `registered_as` column — the same value its act-on-behalf gate checks — so
 * presentation follows the backend. The short "ea" alias is matched defensively only;
 * `account_type` itself emits just the enum values.
 */
export const isAssistantAccountType = (type?: string | null): boolean =>
  type === AccountType.EXECUTIVE_ASSISTANT || type === AccountType.TRAVEL_MANAGER || type === "ea";

/**
 * The account types that belong in the client portal rather than the operator dashboard.
 * Callers pass the authoritative `account_type`, which is null for operator, user and driver
 * accounts. The short "ea" alias is matched defensively only. Presentation only — the backend
 * remains the authority on access.
 */
export const CLIENT_ACCOUNT_TYPES: string[] = [
  AccountType.PASSENGER,
  AccountType.EXECUTIVE_ASSISTANT,
  AccountType.TRAVEL_MANAGER,
  "ea",
];

/**
 * True only when the account type is positively known to be a client one. An unknown, missing or
 * operator type returns false, so anything we can't confidently identify keeps its existing
 * behaviour — the fail-safe direction.
 */
export const isClientAccountType = (type?: string | null): boolean => !!type && CLIENT_ACCOUNT_TYPES.includes(type);
